// src/lib/apiClient.ts — shared authenticated API client
import { getApiBaseUrl } from "./apiBase";
import { safeFetch, readJson, responseError } from './appErrors';

const API_URL = getApiBaseUrl();

let getAccessToken: () => string | null = () => null;
let refreshFn: (() => Promise<string | null>) | null = null;

export function setTokenAccessor(fn: () => string | null) {
  getAccessToken = fn;
}

export function setRefreshFunction(fn: () => Promise<string | null>) {
  refreshFn = fn;
}

type FetchOpts = {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: any;
  headers?: Record<string, string>;
};

export async function api<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const token = getAccessToken();

  const res = await safeFetch(`${API_URL}${path}`, {
    method: opts.method ?? "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 401 && refreshFn) {
    const newToken = await refreshFn();
    if (newToken) {
      const retry = await safeFetch(`${API_URL}${path}`, {
        method: opts.method ?? "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${newToken}`,
          ...(opts.headers || {}),
        },
        body: opts.body ? JSON.stringify(opts.body) : undefined,
      });

      if (retry.status === 401) window.dispatchEvent(new CustomEvent('auth:logout'));
      return readJson<T>(retry,path,opts.method);
    }

    window.dispatchEvent(new CustomEvent("auth:logout"));
    throw responseError(401,{},path,opts.method);
  }

  return readJson<T>(res,path,opts.method);
}

export async function apiForm<T>(path: string, body: FormData, method: "POST" | "PUT" = "POST"): Promise<T> {
  const token = getAccessToken();

  const request = (accessToken: string | null) =>
    safeFetch(`${API_URL}${path}`, {
      method,
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body,
    });

  let res = await request(token);
  if (res.status === 401 && refreshFn) {
    const newToken = await refreshFn();
    if (newToken) res = await request(newToken);
    else {
      window.dispatchEvent(new CustomEvent("auth:logout"));
      throw responseError(401,{},path,method);
    }
  }

  if (res.status === 401) window.dispatchEvent(new CustomEvent('auth:logout'));
  return readJson<T>(res,path,method);
}

export async function apiBlob(path: string): Promise<Blob> {
  const request = (token: string | null) => safeFetch(`${API_URL}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  let res = await request(getAccessToken());
  if (res.status === 401 && refreshFn) {
    const token = await refreshFn();
    if (token) res = await request(token);
  }
  if (!res.ok) return readJson<never>(res, path);
  return res.blob();
}
